import { MainAction } from '@/components/MainAction';
import { Sheet } from '@/components/Sheet';
import { EYEBROW } from '@/components/layout';
import { CountUp } from '@/hooks';
import { cn } from '@/lib/utils';
import { SessionResult } from '@/pages/practice/Session';
import type { Strings } from '@/str';

const STAT = 'flex flex-1 flex-col items-center gap-1 rounded-card border border-edge bg-card py-3';

const RESULT_CELL =
  'flex size-8 items-center justify-center rounded-ctl border border-border bg-card text-xs font-extrabold tabular-nums text-faint data-[mark=ok]:border-ok data-[mark=ok]:bg-ok-tint data-[mark=ok]:text-ok data-[mark=bad]:border-bad data-[mark=bad]:bg-bad-tint data-[mark=bad]:text-bad';

export function BiletResult({
  result,
  total,
  title,
  t,
  onRetake,
  onNext,
  onClose,
}: {
  result: SessionResult;
  total: number;
  title: string;
  t: Strings;
  onRetake: () => void;
  onNext?: () => void;
  onClose: () => void;
}) {
  const pct = total === 0 ? 0 : Math.round((result.correct / total) * 100);
  const band = pct >= 70 ? 'text-ok' : pct >= 45 ? 'text-warn' : 'text-bad';
  const unanswered = total - result.correct - result.wrong;
  const retake = { text: t.retake, run: onRetake };
  const ahead = onNext && { text: t.nextBilet, run: onNext };
  const primary = pct >= 70 && ahead ? ahead : retake;
  const secondary = primary === retake ? ahead : retake;

  return (
    <Sheet title={title} onClose={onClose}>
      <div className="flex flex-col items-center gap-0.5">
        <div
          data-pct={pct}
          className={cn('font-display text-2xl font-extrabold tabular-nums', band)}
        >
          <CountUp to={pct} />%
        </div>
        <div className={EYEBROW}>{t.result}</div>
      </div>

      <div className="flex gap-2">
        <div data-stat="correct" className={STAT}>
          <span className="text-ok text-lg font-extrabold tabular-nums">{result.correct}</span>
          <span className="text-faint text-t12">{t.correct}</span>
        </div>
        <div data-stat="wrong" className={STAT}>
          <span className="text-bad text-lg font-extrabold tabular-nums">{result.wrong}</span>
          <span className="text-faint text-t12">{t.wrong}</span>
        </div>
        <div data-stat="none" className={STAT}>
          <span className="text-faint text-lg font-extrabold tabular-nums">{unanswered}</span>
          <span className="text-faint text-t12">{t.noAnswer}</span>
        </div>
      </div>

      <ol className="m-0 flex list-none flex-wrap justify-center gap-1.5 p-0">
        {result.marks.map((mark, i) => (
          <li
            key={i}
            className={RESULT_CELL}
            data-mark={mark === null ? undefined : mark ? 'ok' : 'bad'}
          >
            {i + 1}
          </li>
        ))}
      </ol>

      {secondary && (
        <button
          type="button"
          className="rounded-card border-border bg-card active:bg-muted min-h-13 w-full border text-base font-extrabold"
          onClick={secondary.run}
        >
          {secondary.text}
        </button>
      )}

      <MainAction text={primary.text} onClick={primary.run} />
    </Sheet>
  );
}
